import memoize from 'lodash/memoize';
import mergeWith from 'lodash/mergeWith';
import isArray from 'lodash/isArray';
import invert from 'lodash/invert';
import { CURRENT_PRODUCT_MODE } from '../constants';
import { FLOW_METRICS_DISPLAY, FRUSTRATION_SIGNAL_METRICS_DISPLAY } from '../flow/meta';

// Custom resolver to create a unique key for memoization
const resolver = (obj1, obj2) => {
  return obj1 + JSON.stringify(obj2);
};

export const mergeObjectsWithSameKey = memoize((obj1, obj2) => {
  return mergeWith({}, obj1, obj2, (value1, value2) => {
    if (isArray(value1) && isArray(value2)) {
      return value1.concat(value2);
    } else {
      return undefined;
    }
  });
}, resolver);

export const createActiveMetricKeys = (metricsMeta) => {
  const activeMetricsKeys = Object.keys(metricsMeta).reduce((acc: any, key: any) => {
    const metricsByCategory = metricsMeta[key];
    const activeMetrics = Object.values(metricsByCategory)
      .filter((metric: any) => metric.active)
      .map((metric: any) => metric.name);
    return {
      ...acc,
      [key]: activeMetrics,
    };
  }, {});
  return activeMetricsKeys;
};

export const createAllMetricKeys = (metricsMeta) => {
  const allMetricKeys = Object.keys(metricsMeta).reduce((acc: any, key: any) => {
    const metricsByCategory = metricsMeta[key];
    const allMetrics = Object.values(metricsByCategory).map((metric: any) => metric.name);
    return {
      ...acc,
      [key]: allMetrics,
    };
  }, {});
  return allMetricKeys;
};

export function getPercentageLocalMetricName(metricName: string) {
  const suffix = 'PercentageLocal';
  if (metricName.endsWith(suffix)) {
    return metricName;
  }
  return metricName + 'PercentageLocal';
}

export const createMetricsMap = (metricsMeta) => {
  const percentageMetricsMap = {};
  const invertPercentageMetricsMap = {};
  const primaryToPercentageLocalKeyMap = {};
  const percentageLocalToPrimaryKeyMap = {};
  const defaultSecondaryMetricsKeyMap = {};
  const invertedDefaultSecondaryMetricsKeyMap = {};
  Object.keys(metricsMeta).forEach((key) => {
    percentageMetricsMap[key] = {};
    invertPercentageMetricsMap[key] = {};
    primaryToPercentageLocalKeyMap[key] = {};
    percentageLocalToPrimaryKeyMap[key] = {};
    defaultSecondaryMetricsKeyMap[key] = {};
    invertedDefaultSecondaryMetricsKeyMap[key] = {};
    const productMetricsMeta = metricsMeta[key] ?? {};
    Object.keys(productMetricsMeta).forEach((metricKey) => {
      const { percentageMetric, calcPercentage, active, analyzable, defaultSecondaryMetric } =
        productMetricsMeta[metricKey] ?? {};
      if (defaultSecondaryMetric) {
        defaultSecondaryMetricsKeyMap[key][metricKey] = defaultSecondaryMetric;
        if (!invertedDefaultSecondaryMetricsKeyMap[key][defaultSecondaryMetric]) {
          invertedDefaultSecondaryMetricsKeyMap[key][defaultSecondaryMetric] = [metricKey];
        } else {
          invertedDefaultSecondaryMetricsKeyMap[key][defaultSecondaryMetric].push(metricKey);
        }
      }
      if (percentageMetric) {
        percentageMetricsMap[key][metricKey] = percentageMetric;
        invertPercentageMetricsMap[key][percentageMetric] = metricKey;
      }
      /** Property analyzable of metric means can analyze and get data of this metric or not */
      // active should be for UI visiblity, analyzable should be for query availability.
      // need to align and refiend with Luigi side when metric meta is served from Luigi.
      if ((active || analyzable) && metricKey && calcPercentage) {
        const percentageLocalName = getPercentageLocalMetricName(metricKey);
        productMetricsMeta[percentageLocalName] = {
          ...(productMetricsMeta?.[metricKey] ?? {}),
          name: percentageLocalName,
          percentageMetric: null,
          active: false,
          calcPercentage: false,
          unit: '%',
        };
        primaryToPercentageLocalKeyMap[key][metricKey] = percentageLocalName;
        percentageLocalToPrimaryKeyMap[key][percentageLocalName] = metricKey;
      }
    });
  });
  return {
    percentageMetricsMap,
    invertPercentageMetricsMap,
    primaryToPercentageLocalKeyMap,
    percentageLocalToPrimaryKeyMap,
    defaultSecondaryMetricsKeyMap,
    invertedDefaultSecondaryMetricsKeyMap,
  };
};

export const createSecondaryOnlyMapping = (metricsMeta, invertedDefaultSecondaryMetricsKeyMap) => {
  const secondaryOnlyMetricsKeyMap = {};
  const invertSecondaryOnlyMetricsKeyMap = {};
  Object.keys(invertedDefaultSecondaryMetricsKeyMap).forEach((key) => {
    secondaryOnlyMetricsKeyMap[key] = {};
    invertSecondaryOnlyMetricsKeyMap[key] = {};
    const productMetrics = invertedDefaultSecondaryMetricsKeyMap[key] ?? {};
    Object.keys(productMetrics).forEach((invertKey) => {
      const { secondaryOnly } = metricsMeta[key][invertKey] ?? {};
      if (secondaryOnly) {
        const metricKeys = productMetrics[invertKey];
        metricKeys.forEach((metricKey) => {
          secondaryOnlyMetricsKeyMap[key][metricKey] = invertKey;
          if (!invertSecondaryOnlyMetricsKeyMap[key][invertKey]) {
            invertSecondaryOnlyMetricsKeyMap[key][invertKey] = [metricKey];
          } else {
            invertSecondaryOnlyMetricsKeyMap[key][invertKey].push(metricKey);
          }
        });
      }
    });
  });
  return {
    secondaryOnlyMetricsKeyMap,
    invertSecondaryOnlyMetricsKeyMap,
  };
};

export const createMetricsGroupedByCategoryForAProduct = ({
  metrics,
  activeOnly = false,
  groupByFlow = true,
}) => {
  const metricsGroupedByCategory = Object.keys(metrics).reduce((acc: any, metricKey: any) => {
    const metric: any = metrics[metricKey];
    if (activeOnly && !metric.active) {
      return acc;
    }
    const category = metric.category;
    if (groupByFlow && metric.flowID) {
      if (!acc[metric.flowID]) {
        acc[metric.flowID] = {};
      }
      acc[metric.flowID][metric.name] = metric;
    } else if (Array.isArray(category)) {
      category.forEach((cat) => {
        if (!acc[cat]) {
          acc[cat] = {};
        }
        acc[cat][metric.name] = metric;
      });
      return acc;
    } else {
      if (!acc[category]) {
        acc[category] = {};
      }
      acc[category][metric.name] = metric;
    }
    return acc;
  }, {});
  return metricsGroupedByCategory;
};

export const createMetricsGroupedByCategory = (metricsMeta) => {
  const metricsGroupedByCategory = {};
  Object.keys(metricsMeta).forEach((key) => {
    const metrics = Object.values(metricsMeta[key]) ?? {};
    const metricsGroup = createMetricsGroupedByCategoryForAProduct({
      metrics,
      activeOnly: false,
      groupByFlow: true,
    });
    metricsGroupedByCategory[key] = metricsGroup;
  });
  return metricsGroupedByCategory;
};

export const createMetricsGroupedByType = (metricsMeta) => {
  const metricsGroupedByType = {};
  Object.keys(metricsMeta).forEach((key) => {
    const contextMetrics = Object.values(metricsMeta[key]);
    const metricsGroup = Object.keys(contextMetrics).reduce((acc: any, metricKey: any) => {
      const metric: any = contextMetrics[metricKey];
      const type = metric.type;
      if (!acc[type]) {
        acc[type] = {};
      }
      acc[type][metric.name] = metric;
      return acc;
    }, {});
    metricsGroupedByType[key] = metricsGroup;
  });
  return metricsGroupedByType;
};

export const getMetricsCategoryKeys = (categoryMeta) => {
  const categoryKeyMaps = Object.keys(categoryMeta).reduce((acc: any, categoryKey: any) => {
    acc[categoryKey] = categoryKey;
    return acc;
  }, {});
  return categoryKeyMaps;
};

export const getMetricCategoryOrder = (categoryMeta) => {
  const categoryOrderDetails = Object.keys(categoryMeta)
    .sort((a, b) => {
      return categoryMeta[a].order - categoryMeta[b].order;
    })
    .map((categoryKey) => categoryKey);
  return categoryOrderDetails;
};

export const createFlowMetricsMeta = (metricsGroupedByCategory: any, flowConfigMeta: any) => {
  const result = { app: {} };
  if (CURRENT_PRODUCT_MODE === 'APP') {
    const metricsMeta = {};
    Object.keys(flowConfigMeta).forEach((flowKey: any) => {
      const flowMetrics = metricsGroupedByCategory.app[flowKey]
        ? Object.values(metricsGroupedByCategory.app[flowKey])
        : [];
      if (!flowMetrics.length) return;
      const metricsMapping = invert(flowConfigMeta[flowKey].metrics);
      const sortedFlowMetrics = [...flowMetrics].sort((a: any, b: any) =>
        a.name.localeCompare(b.name),
      );
      sortedFlowMetrics.forEach((metric: any) => {
        metricsMeta[metric.name] = {
          ...metric,
          percentageMetric: metric.percentageMetric,
          displayName: FLOW_METRICS_DISPLAY[metricsMapping[metric.id]]?.name || metric.displayName,
          short: FLOW_METRICS_DISPLAY[metricsMapping[metric.id]]?.name || metric.short,
        };
      });
    });

    Object.keys(metricsMeta).forEach((metricKey) => {
      const metric = metricsMeta[metricKey];
      const { percentageMetric } = metric;
      if (percentageMetric && metricsMeta[percentageMetric]) {
        metricsMeta[percentageMetric] = {
          ...metricsMeta[percentageMetric],
          displayName: metric.displayName,
          short: metric.short,
        };
      }
    });

    result.app = { ...metricsMeta };
  }
  return result;
};

export const createFrustrationSignalMetricsMeta = (
  metricsGroupedByCategory: any,
  frustrationSignalConfigMeta: any,
) => {
  const result = { app: {} };
  if (CURRENT_PRODUCT_MODE === 'APP' && Object.keys(frustrationSignalConfigMeta).length) {
    const metricsMeta = {};
    const frustrationSignalMetrics = metricsGroupedByCategory.app['ECO_FRUSTRATION_SIGNALS'];
    Object.keys(frustrationSignalConfigMeta).forEach((signalKey: any) => {
      const metricsMapping = invert(frustrationSignalConfigMeta[signalKey].metrics);
      const metricKeys = Object.keys(metricsMapping);
      if (metricKeys && metricKeys.length) {
        metricKeys.forEach((metricKey: any) => {
          metricsMeta[metricKey] = {
            ...frustrationSignalMetrics[metricKey],
            displayName:
              // First check local signal config metricsDisplay, then fallback to global FRUSTRATION_SIGNAL_METRICS_DISPLAY
              frustrationSignalConfigMeta[signalKey].metricsDisplay?.[metricsMapping[metricKey]]
                ?.name ||
              FRUSTRATION_SIGNAL_METRICS_DISPLAY[metricsMapping[metricKey]]?.name ||
              frustrationSignalMetrics[metricKey].displayName,
            short:
              // First check local signal config metricsDisplay, then fallback to global FRUSTRATION_SIGNAL_METRICS_DISPLAY
              frustrationSignalConfigMeta[signalKey].metricsDisplay?.[metricsMapping[metricKey]]
                ?.shortName ||
              FRUSTRATION_SIGNAL_METRICS_DISPLAY[metricsMapping[metricKey]]?.shortName ||
              frustrationSignalMetrics[metricKey].short,
          };
        });
      }
    });

    Object.keys(metricsMeta).forEach((metricKey) => {
      const metric = metricsMeta[metricKey];
      const { percentageMetric } = metric;
      if (percentageMetric && metricsMeta[percentageMetric]) {
        metricsMeta[percentageMetric] = {
          ...metricsMeta[percentageMetric],
          displayName: metric.displayName,
          short: metric.short,
        };
      }
    });

    // Also handle reverse mapping - find percentage metrics that should inherit from frustration signal base metrics
    const frustrationSignalMetricKeys = Object.keys(metricsMeta);
    Object.keys(frustrationSignalMetrics).forEach((originalMetricKey) => {
      const originalMetric = frustrationSignalMetrics[originalMetricKey];
      const { percentageMetric } = originalMetric;

      // If this original metric has a percentage metric and we have frustration signal override for the base metric
      if (percentageMetric && frustrationSignalMetricKeys.includes(originalMetricKey)) {
        const baseMetricWithOverride = metricsMeta[originalMetricKey];
        // Apply the frustration signal display names to the percentage metric too
        if (baseMetricWithOverride && frustrationSignalMetrics[percentageMetric]) {
          metricsMeta[percentageMetric] = {
            ...frustrationSignalMetrics[percentageMetric],
            displayName: baseMetricWithOverride.displayName,
            short: baseMetricWithOverride.short,
          };
        }
      }
    });

    result.app = { ...metricsMeta };
  }
  return result;
};

export const createPercentileMetrics = (metricsMeta) => {
  const percentileMetrics = {};
  Object.keys(metricsMeta).forEach((key) => {
    const productMetrics = metricsMeta[key] ?? {};
    percentileMetrics[key] = [];
    Object.keys(productMetrics).forEach((metricKey) => {
      const metric: any = productMetrics[metricKey];
      const { supportsPercentiles } = metric ?? {};
      if (supportsPercentiles) {
        percentileMetrics[key].push(metricKey);
      }
    });
  });
  return percentileMetrics;
};
